import { setFilterBarTestId as setDataTableFilterBarTestId } from "@bcc/components/data-table/data-table.utils";
import { setAddProductSelectionModalTestId } from "@bcc/components/products/add-product-selection-modal.utils";
import { setManualAddProductModalTestId } from "@bcc/components/products/manual-add-product-modal.utils";
import { setProductSyncModalTestId } from "@bcc/components/products/product-sync-modal/product-sync-modal.utils";
import { setShopifyImportModalTestId } from "@bcc/components/products/shopify-import-modal/shopify-import-modal.utils";
import { setProductsViewTestId } from "@bcc/components/views/products-view.utils";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { setSidebarNavTourId, TOUR_IDS } from "../tour-ids";
import type { FeatureTour } from "../tour.types";
import { DATA_TABLE_ROW_SELECTOR, testIdSelector, tourTargetSelector } from "../tour.utils";

/** Dialog steps skip quickly when the trigger is absent/disabled (SS roasters, no installation) */
const DIALOG_STEP_TIMEOUT_MS = 4000;

const PRODUCTS_TABLE = tourTargetSelector(TOUR_IDS.products.table);
const MAP_PRODUCTS_BUTTON = testIdSelector(setProductsViewTestId("map-products-button"));
const ADD_PRODUCT_BUTTON = testIdSelector(setProductsViewTestId("add-product-button"));
const SHOPIFY_IMPORT_MODAL = testIdSelector(setShopifyImportModalTestId("content"));

/** Closes the mapping dialog when it is still open */
const CANCEL_PRODUCT_SYNC = { clickTarget: testIdSelector(setProductSyncModalTestId("cancel-button")) };

/** Closes the import modal via its Radix close button */
const CLOSE_SHOPIFY_IMPORT = { clickTarget: `${SHOPIFY_IMPORT_MODAL} [data-slot="dialog-close"]` };

export const productsTour: FeatureTour = {
  id: "products",
  titleKey: "tour.features.products.title",
  order: 4,
  requiredModule: "products",
  requiredPage: "products.view",
  steps: [
    {
      id: "products.nav",
      route: ROUTE_PATHS.PRODUCTS,
      target: tourTargetSelector(setSidebarNavTourId("products")),
      titleKey: "tour.steps.products.nav.title",
      bodyKey: "tour.steps.products.nav.body",
      placement: "right",
    },
    {
      id: "products.search-filters",
      route: ROUTE_PATHS.PRODUCTS,
      target: tourTargetSelector(TOUR_IDS.products.searchFilters),
      // The filter chips live in the table's filter bar, a separate subtree
      // from the search box — spotlight both together
      extendTargets: [`${PRODUCTS_TABLE} ${testIdSelector(setDataTableFilterBarTestId())}`],
      titleKey: "tour.steps.products.searchFilters.title",
      bodyKey: "tour.steps.products.searchFilters.body",
      placement: "bottom",
    },
    {
      id: "products.table",
      showsSampleData: true,
      route: ROUTE_PATHS.PRODUCTS,
      target: PRODUCTS_TABLE,
      titleKey: "tour.steps.products.table.title",
      bodyKey: "tour.steps.products.table.body",
      placement: "top",
      requiredPage: "products.edit",
      emptyState: {
        detectTarget: `${PRODUCTS_TABLE} ${DATA_TABLE_ROW_SELECTOR}`,
        bodyKey: "tour.steps.products.table.emptyBody",
      },
    },
    {
      id: "products.map-products-button",
      route: ROUTE_PATHS.PRODUCTS,
      target: MAP_PRODUCTS_BUTTON,
      titleKey: "tour.steps.products.mapProductsButton.title",
      bodyKey: "tour.steps.products.mapProductsButton.body",
      placement: "bottom",
      requiredPage: "products.edit",
      // Closes the mapping dialog when the user steps Back out of it
      prepare: [CANCEL_PRODUCT_SYNC],
      // SS roasters don't get this button at all — skip instantly instead of waiting out the timeout
      skipUnless: MAP_PRODUCTS_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.map-products",
      showsSampleData: true,
      route: ROUTE_PATHS.PRODUCTS,
      target: testIdSelector(setProductSyncModalTestId("content")),
      titleKey: "tour.steps.products.mapProducts.title",
      bodyKey: "tour.steps.products.mapProducts.body",
      placement: "right",
      requiredPage: "products.edit",
      prepare: [{ clickTarget: MAP_PRODUCTS_BUTTON }],
      // The dialog can only open from its button (absent for SS roasters / no installation)
      skipUnless: MAP_PRODUCTS_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.add-product-button",
      route: ROUTE_PATHS.PRODUCTS,
      target: ADD_PRODUCT_BUTTON,
      titleKey: "tour.steps.products.addProductButton.title",
      bodyKey: "tour.steps.products.addProductButton.body",
      placement: "bottom",
      requiredPage: "products.create",
      // Closes whichever dialog is still open: the mapping dialog when moving
      // forward from map-products, the import modal when stepping Back
      prepare: [CANCEL_PRODUCT_SYNC, CLOSE_SHOPIFY_IMPORT],
      // SS roasters don't get this button at all — skip instantly instead of waiting out the timeout
      skipUnless: ADD_PRODUCT_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
    {
      id: "products.add-product",
      showsSampleData: true,
      route: ROUTE_PATHS.PRODUCTS,
      // Whichever add-product dialog the vendor's setup opens
      target: [
        SHOPIFY_IMPORT_MODAL,
        testIdSelector(setAddProductSelectionModalTestId("content")),
        testIdSelector(setManualAddProductModalTestId("content")),
      ].join(", "),
      titleKey: "tour.steps.products.addProduct.title",
      bodyKey: "tour.steps.products.addProduct.body",
      placement: "right",
      requiredPage: "products.create",
      prepare: [CANCEL_PRODUCT_SYNC, { clickTarget: ADD_PRODUCT_BUTTON }],
      skipUnless: ADD_PRODUCT_BUTTON,
      targetTimeoutMs: DIALOG_STEP_TIMEOUT_MS,
    },
  ],
};
